import type { Aabb, Vec2 } from "@townsy/contracts";
import { aabbInflate, clamp } from "./geometry";
import { colliderList, collidersNear, type ColliderSet } from "./spatialIndex";

export interface CircleBody {
  x: number;
  z: number;
  radius: number;
}

/**
 * True when a circle intersects an axis-aligned box (closest-point test).
 */
export function circleIntersectsAabb(c: CircleBody, b: Aabb): boolean {
  const cx = clamp(c.x, b.minX, b.maxX);
  const cz = clamp(c.z, b.minZ, b.maxZ);
  const dx = c.x - cx;
  const dz = c.z - cz;
  return dx * dx + dz * dz < c.radius * c.radius;
}

/** True when a circle intersects any collider (a list, or a ColliderIndex: same answer, fewer boxes tested). */
export function pointInsideAny(x: number, z: number, radius: number, colliders: ColliderSet): boolean {
  for (const b of collidersNear(colliders, x - radius, z - radius, x + radius, z + radius)) {
    if (circleIntersectsAabb({ x, z, radius }, b)) return true;
  }
  return false;
}

/**
 * Move a circle by `delta`, resolving collisions axis by axis so the body slides along walls and
 * stops cleanly at corners. Each axis uses the inflated box (Minkowski sum), which gives exact
 * results for axis-aligned geometry. `colliders` may be a ColliderIndex (world v3): each sweep then
 * tests only the boxes near its path, with exactly the same result as scanning the whole list.
 */
export function moveWithSliding(start: Vec2, delta: Vec2, radius: number, colliders: ColliderSet, bounds: Aabb): Vec2 {
  let x = start.x;
  let z = start.z;

  // --- X axis ---
  if (delta.x !== 0) {
    let nx = x + delta.x;
    for (const b of collidersNear(colliders, Math.min(x, nx) - radius, z - radius, Math.max(x, nx) + radius, z + radius)) {
      const e = aabbInflate(b, radius);
      if (z <= e.minZ || z >= e.maxZ) continue; // not overlapping on z
      if (delta.x > 0 && x <= e.minX && nx > e.minX) nx = e.minX;
      else if (delta.x < 0 && x >= e.maxX && nx < e.maxX) nx = e.maxX;
    }
    x = nx;
  }

  // --- Z axis ---
  if (delta.z !== 0) {
    let nz = z + delta.z;
    for (const b of collidersNear(colliders, x - radius, Math.min(z, nz) - radius, x + radius, Math.max(z, nz) + radius)) {
      const e = aabbInflate(b, radius);
      if (x <= e.minX || x >= e.maxX) continue; // not overlapping on x
      if (delta.z > 0 && z <= e.minZ && nz > e.minZ) nz = e.minZ;
      else if (delta.z < 0 && z >= e.maxZ && nz < e.maxZ) nz = e.maxZ;
    }
    z = nz;
  }

  // Hard world bounds (also covered by boundary colliders, but never trust a single mechanism).
  x = clamp(x, bounds.minX + radius, bounds.maxX - radius);
  z = clamp(z, bounds.minZ + radius, bounds.maxZ - radius);

  // If we somehow ended inside a box (spawned there, or geometry changed under us), push out. Rare:
  // only then are all boxes walked, in their original order, so the result matches a plain list.
  if (!pointInsideAny(x, z, radius, colliders)) return { x, z };
  for (const b of colliderList(colliders)) {
    if (!circleIntersectsAabb({ x, z, radius }, b)) continue;
    const e = aabbInflate(b, radius);
    const pushLeft = x - e.minX;
    const pushRight = e.maxX - x;
    const pushDown = z - e.minZ;
    const pushUp = e.maxZ - z;
    const m = Math.min(pushLeft, pushRight, pushDown, pushUp);
    if (m === pushLeft) x = e.minX;
    else if (m === pushRight) x = e.maxX;
    else if (m === pushDown) z = e.minZ;
    else z = e.maxZ;
  }

  return { x, z };
}

/** The thin wall ring around an interior's floor bounds (1 m thick boxes just outside the floor). */
export function interiorWallColliders(t: { bounds: Aabb }): Aabb[] {
  const b = t.bounds;
  const T = 1;
  return [
    { minX: b.minX - T, minZ: b.minZ - T, maxX: b.minX, maxZ: b.maxZ + T },
    { minX: b.maxX, minZ: b.minZ - T, maxX: b.maxX + T, maxZ: b.maxZ + T },
    { minX: b.minX - T, minZ: b.minZ - T, maxX: b.maxX + T, maxZ: b.minZ },
    { minX: b.minX - T, minZ: b.maxZ, maxX: b.maxX + T, maxZ: b.maxZ + T },
  ];
}
